import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PartnersService } from '../../services/partners.service';
import { CamapignService } from '../../services/campaign.service';
import { PartnerProductsService } from '../../services/partner-products.service';
import { PersonsService } from '../../services/persons.service';
import { Partner } from '../../interfaces/partners.interface';
import { Camapign } from '../../interfaces/campaigns.interface';
import { PartnerProduct } from '../../interfaces/partner-product.interface';
import { Person, PersonCamapignAggregation } from '../../interfaces/persons.interface';
import { Page } from '../../interfaces/pages.interface';
import { PagesService } from '../../services/pages.service';

@Component({
  selector: 'app-awards',
  standalone: false,
  templateUrl: './awards.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./awards.component.scss']
})
export class AwardsComponent implements OnInit, OnDestroy {

  partner: Partner | null = null;
  partnerLoading = false;
  partnerError: string | null = null;

  imagesUrl = `${environment.ImagesUrl}/partners/`;

  campaigns: Camapign[] = [];
  campaignsLoading = false;

  products: PartnerProduct[] = [];
  productsLoading = false;

  // Score filter properties
  minScore: number = 0;
  maxScore: number = 10000;
  scoreRangeMin: number = 0;
  scoreRangeMax: number = 10000;

  persons: Person[] = [];
  personsLoading = false;

  page: Page | null = null;

  // Accordion state
  openAccordionIndex: number | null = 0;  // First item open by default

  private $destroy = new Subject<void>();

  constructor(
    private partnersService: PartnersService,
    private campaignService: CamapignService,
    private partnerProductsService: PartnerProductsService,
    private pagesService: PagesService,
    private personsService: PersonsService
  ) {}

  ngOnInit(): void {
    this.loadMainPartner();
    this.loadPersons();

    this.pagesService.getPage('nagrody')
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (data) => { this.page = data; },
        error: () => { /* ignore page loading errors */ }
      });
  }

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  private loadMainPartner(): void {
    this.partnerLoading = true;
    this.partnerError = null;

    this.partnersService.getData({ is_main: 1, limit: 1 })
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (res) => {
          this.partner = res.items?.[0] ?? null;
          this.partnerLoading = false;

          if (this.partner) {
            this.loadCampaigns(this.partner.id);
            this.loadProducts(this.partner.id);
          }
        },
        error: () => {
          this.partnerError = 'Nie udało się załadować danych partnera.';
          this.partnerLoading = false;
        }
      });
  }

  private loadCampaigns(partnerId: number): void {
    this.campaignsLoading = true;
    this.campaignService.getCurrentList({ partner_id: partnerId })
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (res) => {
          this.campaigns = res.items || [];
          this.campaignsLoading = false;
        },
        error: () => { this.campaignsLoading = false; }
      });
  }

  private loadProducts(partnerId: number): void {
    this.productsLoading = true;
    this.partnerProductsService.getCurrentList({
      partner_id: partnerId,
      order_by: 'sort',
      order: 'desc'
    }).pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (res) => {
          this.products = res.items || [];
          this.calculateScoreRange();
          this.productsLoading = false;
        },
        error: () => { this.productsLoading = false; }
      });
  }

  /**
   * Calculate min/max scores from products and update slider options
   */
  private calculateScoreRange(): void {
    const MIN_DIFFERENCE = 10;
    
    if (this.products.length === 0) {
      this.minScore = 0;
      this.maxScore = 10000;
      this.scoreRangeMin = 0;
      this.scoreRangeMax = 10000;
    } else {
      const scores = this.products
        .map(p => p.score ?? 0)
        .filter(score => score > 0);
      
      if (scores.length > 0) {
        this.minScore = Math.min(...scores);
        this.maxScore = Math.max(...scores);
        
        // Ensure minimum difference of 10 points
        if (this.maxScore - this.minScore < MIN_DIFFERENCE) {
          this.maxScore = this.minScore + MIN_DIFFERENCE;
        }
        
        this.scoreRangeMin = this.minScore;
        this.scoreRangeMax = this.maxScore;
      } else {
        this.minScore = 0;
        this.maxScore = 10000;
        this.scoreRangeMin = 0;
        this.scoreRangeMax = 10000;
      }
    }
  }

  /**
   * Get filtered products based on score range
   */
  get filteredProducts(): PartnerProduct[] {
    return this.products.filter(product => {
      const score = product.score ?? 0;
      return score >= this.scoreRangeMin && score <= this.scoreRangeMax;
    });
  }

  /**
   * Handle slider value change
   */
  onScoreRangeChange(): void {
    // Ensure min doesn't exceed max
    if (this.scoreRangeMin > this.scoreRangeMax) {
      [this.scoreRangeMin, this.scoreRangeMax] = [this.scoreRangeMax, this.scoreRangeMin];
    }
  }

  /**
   * Format score value for display
   */
  formatScore(value: number): string {
    return value.toLocaleString('pl-PL') + ' pkt';
  }

  private loadPersons(): void {
    this.personsLoading = true;
    this.personsService.getCurrent()
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (data) => {
          this.persons = data;
          this.personsLoading = false;
        },
        error: () => { this.personsLoading = false; }
      });
  }

  getPartnerAggregation(person: Person): PersonCamapignAggregation | null {
    if (!this.partner || !person.aggregations?.length) return null;
    return person.aggregations.find(a => a.partner_id === this.partner!.id) ?? null;
  }

  getPersonsForPartner(): Person[] {
    return this.persons.filter(p => this.getPartnerAggregation(p) !== null);
  }

  /** Aggregation with partner_id === 0 holds the global totals across all partners. */
  getGlobalAggregation(person: Person): PersonCamapignAggregation | null {
    return person.aggregations?.find(a => a.partner_id === 0) ?? null;
  }

  getPersonsWithGlobalPoints(): Person[] {
    return this.persons.filter(p => this.getGlobalAggregation(p) !== null);
  }

  /** All per-partner aggregations (partner_id > 0). */
  getPartnerAggregations(person: Person): PersonCamapignAggregation[] {
    return person.aggregations?.filter(a => (a.partner_id ?? 0) > 0) ?? [];
  }

  getContactItems(): Array<{ type: 'phone' | 'email' | 'text'; value: string }> {
    if (!this.partner?.contact) return [];
    return this.partner.contact
      .split(/[\n;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(part => {
        if (this.isEmail(part)) return { type: 'email' as const, value: part };
        if (this.isPhone(part)) return { type: 'phone' as const, value: part };
        return { type: 'text' as const, value: part };
      });
  }

  private isEmail(s: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  private isPhone(s: string): boolean {
    return /^[\+\d][\d\s\-\(\)\.\/]{5,}$/.test(s);
  }

  toTelHref(phone: string): string {
    return 'tel:' + phone.replace(/[^\d\+]/g, '');
  }

  cleanContactText(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&ndash;/gi, ' - ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&oacute;/gi, 'ó')
      .trim();
  }

  /**
   * Toggle accordion item - only one item can be open at a time
   */
  toggleAccordion(index: number): void {
    if (this.openAccordionIndex === index) {
      this.openAccordionIndex = null; // Close if already open
    } else {
      this.openAccordionIndex = index; // Open clicked item
    }
  }

  /**
   * Check if accordion item is open
   */
  isAccordionOpen(index: number): boolean {
    return this.openAccordionIndex === index;
  }
}
